import { useTimelineContext } from 'dnd-timeline';
import type { ReactNode } from 'react';
import { type ScheduleView, dayBands, mytDateOf, mytDayLabel, ticksFor } from './dropLogic.js';

/**
 * 316 — the axis above the rows, in Malaysia time (ticks from `dropLogic.ticksFor`). #631: in Week and
 * Month a weekend day's cell carries the same tint as its column, and today's label is marked.
 *
 * 329 (FR-004): the corner cell above the car names carries the timezone and counts line. Only the
 * ticks are hidden from screen readers; the corner names the zone and stays readable.
 *
 * 368 (#1236, FR-004): in Week and Month each day heading is a button that opens that day in the Day view.
 */
export function TimeAxis({
  view,
  now,
  corner,
  onOpenDay,
}: {
  view: ScheduleView;
  now: number;
  corner?: ReactNode;
  onOpenDay?: (date: string) => void;
}) {
  const { range, valueToPixels, sidebarWidth } = useTimelineContext();
  const ticks = ticksFor(range, view);
  const bandAt = new Map(dayBands(range, view, now).map((b) => [b.start, b]));
  return (
    <div
      data-testid="schedule-axis"
      className="sticky top-0 z-10 flex h-8 border-b border-hairline bg-surface"
    >
      <div
        style={{ width: sidebarWidth }}
        className="flex shrink-0 items-center overflow-hidden border-r border-hairline px-2"
      >
        {corner}
      </div>
      <div className="relative flex-1">
        {ticks.map((t) => {
          const band = bandAt.get(t.at);
          // 368: a day heading opens the Day view (Week and Month only; Day's ticks are hours).
          const opensDay = view !== 'day' && onOpenDay !== undefined;
          const Tag = opensDay ? 'button' : 'span';
          return (
            <Tag
              key={t.at}
              {...(opensDay
                ? {
                    type: 'button' as const,
                    'aria-label': `Open ${mytDayLabel(t.at)} in the Day view`,
                    onClick: () => onOpenDay(mytDateOf(t.at)),
                  }
                : { 'aria-hidden': true })}
              data-day-kind={band?.kind}
              className={[
                'absolute top-0 h-full border-l pl-1 pt-1.5 text-[11px] whitespace-nowrap',
                t.major ? 'border-hairline-strong font-semibold' : 'border-hairline',
                // One text colour per cell (two would be decided by stylesheet order, not here).
                band?.kind === 'today'
                  ? 'bg-surface-feature font-semibold text-brand-blue'
                  : t.major
                    ? 'text-ink'
                    : 'text-steel',
                band?.kind === 'weekend' ? 'bg-surface-soft' : '',
                opensDay
                  ? 'cursor-pointer text-left hover:underline focus-visible:outline-2 focus-visible:outline-brand-blue'
                  : '',
              ].join(' ')}
              style={{
                left: valueToPixels(t.at - range.start),
                ...(band ? { width: valueToPixels(band.end - t.at) } : {}),
              }}
            >
              {t.label}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}
